<template>
  <base-wrapper>
    <div class="space-y-4">
      <div class="flex items-center justify-end gap-x-2">
        <base-input
          :shadow="false"
          placeholder="Cari Hak Akses"
          type="search"
          debounce
          v-on:native-input="onSearch"
          v-model="filter.search"
        />
        <base-select
          :shadow="false"
          :options="[
            { key: 'null', value: null, label: 'Tipe Kantor' },
            { key: 'pusat', value: 'pusat', label: 'Pusat' },
            {
              key: 'kantor_pelayanan',
              value: 'kantor_pelayanan',
              label: 'Kantor Pelayanan',
            },
            {
              key: 'stockist_center',
              value: 'stockist_center',
              label: 'Stockist Center',
            },
            { key: 'stockist', value: 'stockist', label: 'Stockist' },
            { key: 'member', value: 'member', label: 'Mitra Usaha' },
          ]"
          v-model="filter.office_type"
          @change="onSearch"
        />
        <base-button v-if="allowAction" @click="onCreate">Tambah Hak Akses</base-button>
        <base-button :loading="loadingExport.visible" @click="onExport">
          <Icon
            v-if="!loadingExport.visible"
            icon="heroicons:arrow-down-tray"
            class="h-4 w-4"
          />
          {{ loadingExport.visible ? loadingExport.text : 'Export' }}
        </base-button>
      </div>
      <datatable
        :columns="columns"
        :scroll-x="false"
        :total="roles.meta.page.total"
        :perPage="roles.meta.page.perPage"
        :currentPage="roles.meta.page.currentPage"
        @pagechanged="onPageChanged"
      >
        <template v-slot:tbody="{ classes }">
          <tr
            v-for="role in roles.data"
            :key="role.id"
            :class="[classes.tr, 'bg-white hover:bg-gray-100']"
          >
            <td
              @click="onOpenDetail(role)"
              :class="[classes.td, 'cursor-pointer']"
            >
              {{ role.attributes.name }}
            </td>
            <td
              @click="onOpenDetail(role)"
              :class="[classes.td, 'cursor-pointer']"
            >
              {{ officeTypeLabels[role.attributes.office_type] }}
            </td>
            <td
              @click="onOpenDetail(role)"
              :class="[classes.td, 'cursor-pointer']"
            >
              {{ role.attributes.description | truncate(100) }}
            </td>
            <td
              @click="onOpenDetail(role)"
              :class="[classes.td, 'cursor-pointer']"
            >
              {{ role.attributes.users_count }}
            </td>
            <td :class="[classes.td]">
              <div v-if="allowAction" class="flex gap-1">
                <button
                  class="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
                  @click="onEdit(role)"
                >
                  <Icon icon="heroicons:pencil-square-solid" class="w-4 h-4" />
                </button>
                <button
                  v-if="!role.attributes.users_count"
                  class="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
                  @click="onDelete(role)"
                >
                  <Icon icon="heroicons:trash-solid" class="w-4 h-4" />
                </button>
              </div>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <role-create-modal
      :visible="createModalVisible"
      @close="createModalVisible = false"
      @success="onSuccessCreate"
    />
    <role-edit-modal
      :visible="editModal.visible"
      :role="editModal.role"
      @close="editModal.visible = false"
      @success="onSuccessEdit"
    />
    <role-detail-modal
      :visible="detailModal.visible"
      :role-id="detailModal.roleId"
      :reload="detailModal.reload"
      @close="onCloseDetail"
      @attach-menu="onAttachMenu"
      @detail-permission="onDetailMenuPermission"
      @view-user="onViewRoleUser"
    />
    <role-attach-menu-modal
      :visible="attachMenuModal.visible"
      :role-id="attachMenuModal.roleId"
      @close="onCloseAttachMenu"
    />
    <role-menu-permission-modal
      :visible="menuPermissionModal.visible"
      :reload="menuPermissionModal.reload"
      :menu-id="menuPermissionModal.menuId"
      :role-id="menuPermissionModal.roleId"
      @attach-permission="onAttachMenuPermission"
      @close="onCloseDetailMenuPermission"
    />
    <role-attach-menu-permission-modal
      :visible="attachMenuPermissionModal.visible"
      :menu-id="attachMenuPermissionModal.menuId"
      :role-id="attachMenuPermissionModal.roleId"
      @attach-permission="onAttachMenuPermission"
      @close="onCloseAttachMenuPermission"
    />
    <role-user-modal
      :visible="roleUserModal.visible"
      :role-id="roleUserModal.roleId"
      :reload="roleUserModal.reload"
      @close="onCloseRoleUser"
      @delete-user="onDeleteUser"
      @delete-all-user="onDeleteAllUser"
    />
    <BaseConfirm
      :visible="roleDeleteModal.visible"
      title="Hapus Hak Akses"
      message="Apakah anda yakin ingin menghapus hak akses ini?"
      icon-type="badge"
      @close="roleDeleteModal.visible = false"
      @confirmed="onConfirmDelete"
    />
    <BaseConfirm
      :visible="userDeleteModal.visible"
      :title="userDeleteModal.userId ? 'Hapus Pengguna' : 'Hapus Semua Pengguna'"
      :message="userDeleteModal.userId ? 'Apakah anda yakin ingin menghapus pengguna ini?' : 'Apakah anda yakin ingin menghapus semua pengguna di role ini?'"
      icon-type="badge"
      @close="onCloseConfirmDeleteUser"
      @confirmed="onConfirmDeleteUser"
    />

    <loading v-if="loading || loadingJobStatus" />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import RoleDetailModal from '@/components/role/role-detail-modal.vue';
import RoleAttachMenuModal from '@/components/role/role-attach-menu-modal.vue';
import RoleCreateModal from '@/components/role/role-create-modal.vue';
import RoleEditModal from '@/components/role/role-edit-modal.vue';
import RoleMenuPermissionModal from '@/components/role/role-menu-permission-modal.vue';
import RoleAttachMenuPermissionModal from '@/components/role/role-attach-menu-permission-modal.vue';
import RoleUserModal from '@/components/role/role-user-modal.vue';
import BaseConfirm from '@/components/base/BaseConfirm.vue'
import { mapGetters } from 'vuex'
import { mapActions } from 'vuex';
import { downloadFileUrl } from '../../../../../services/utils.service';

export default {
  mixins: [requestMixin],
  components: {
    RoleDetailModal,
    RoleAttachMenuModal,
    RoleCreateModal,
    RoleMenuPermissionModal,
    RoleAttachMenuPermissionModal,
    RoleEditModal,
    RoleUserModal,
    BaseConfirm
  },
  data() {
    return {
      loading: false,
      roles: {
        data: [],
        meta: {
          page: {},
        },
      },
      filter: {
        search: null,
        office_type: null,
      },
      createModalVisible: false,
      editModal: {
        visible: false,
        role: null,
      },
      detailModal: {
        visible: false,
        reload: true,
        roleId: null,
      },
      attachMenuModal: {
        visible: false,
        roleId: null,
      },
      roleUserModal: {
        visible: false,
        roleId: null,
        reload: true
      },
      roleDeleteModal: {
        visible: false,
        roleId: null,
      },
      userDeleteModal: {
        visible: false,
        userId: null,
      },
      menuPermissionModal: {
        visible: false,
        reload: true,
        roleId: null,
        menuId: null,
      },
      attachMenuPermissionModal: {
        visible: false,
        roleId: null,
        menuId: null,
      },
      loadingJobStatus: false,
      loadingExport: {
        visible: false,
        text: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      checkHasPermission: 'auth/checkHasPermission',
      getChannelName: 'auth/getChannelName',
    }),
    allowAction() {
      return this.me.current_role.toLowerCase() === 'superadmin'
    },
    columns() {
      return [
        { id: 'name', name: 'Nama' },
        { id: 'office_type', name: 'Tipe Kantor' },
        { id: 'description', name: 'Deskripsi' },
        { id: 'users', name: 'Total Pengguna' },
        { id: 'action', name: 'Aksi' },
      ];
    },
    officeTypeLabels() {
      return {
        pusat: 'Pusat',
        supplier: 'Supplier',
        kantor_pelayanan: 'Kantor Pelayanan',
        stockist_center: 'Stockist Center',
        stockist: 'Stockist',
        member: 'Mitra Usaha',
      };
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    onPageChanged(page) {
      this.loadRoles({
        'page[number]': page,
      });
    },
    onSearch() {
      this.loadRoles();
    },
    onCreate() {
      this.createModalVisible = true;
    },
    onSuccessCreate() {
      this.loadRoles();
    },
    onEdit(role) {
      this.editModal.role = role;
      this.editModal.visible = true;
    },
    onDelete(role) {
      this.roleDeleteModal.roleId = role.id;
      this.roleDeleteModal.visible = true;
    },
    onSuccessEdit() {
      this.loadRoles();
    },
    onOpenDetail(role) {
      this.detailModal.roleId = role.id;
      this.detailModal.visible = true;
    },
    onCloseDetail() {
      this.detailModal.reload = true;
      this.detailModal.visible = false;
    },
    onAttachMenu(role) {
      this.detailModal.visible = false;

      this.attachMenuModal.roleId = role.id;
      this.attachMenuModal.visible = true;
    },
    onViewRoleUser(role) {
      this.detailModal.visible = false;

      this.roleUserModal.roleId = role.id;
      this.roleUserModal.reload = true;
      this.roleUserModal.visible = true;
    },
    onCloseAttachMenu(options = {}) {
      this.attachMenuModal.visible = false;

      this.detailModal.reload = options?.reload ?? false;
      this.detailModal.visible = true;
    },
    onCloseRoleUser(options = {}) {
      this.roleUserModal.visible = false;

      this.detailModal.reload = options?.reload ?? false;
      this.detailModal.visible = true;
    },
    onDeleteUser(userId) {
      this.roleUserModal.reload = false;
      this.roleUserModal.visible = false

      this.userDeleteModal.userId = userId
      this.userDeleteModal.visible = true
    },
    onDeleteAllUser() {
      this.roleUserModal.reload = false;
      this.roleUserModal.visible = false

      this.userDeleteModal.userId = null
      this.userDeleteModal.visible = true
    },
    onDetailMenuPermission({ menu, role }) {
      this.detailModal.visible = false;

      this.menuPermissionModal.menuId = menu.id;
      this.menuPermissionModal.roleId = role.id;
      this.menuPermissionModal.visible = true;
    },
    onCloseDetailMenuPermission() {
      this.menuPermissionModal.visible = false;
      this.menuPermissionModal.reload = true;

      this.detailModal.reload = false;
      this.detailModal.visible = true;
    },
    onAttachMenuPermission() {
      this.menuPermissionModal.visible = false;

      this.attachMenuPermissionModal.menuId = this.menuPermissionModal.menuId;
      this.attachMenuPermissionModal.roleId = this.menuPermissionModal.roleId;
      this.attachMenuPermissionModal.visible = true;
    },
    onCloseAttachMenuPermission(options = {}) {
      this.attachMenuPermissionModal.visible = false;

      this.menuPermissionModal.reload = options?.reload ?? false;
      this.menuPermissionModal.visible = true;
    },
    onCloseConfirmDeleteUser() {
      this.userDeleteModal.visible = false

      this.roleUserModal.visible = true
    },
    async onConfirmDelete() {
      this.loading = true;

      const [, error] = await this.request(`/api/v1/roles/${this.roleDeleteModal.roleId}`, {
        method: 'delete' 
      });

      if (!error) {
        this.loadRoles()
        this.roleDeleteModal.visible = false
      } else {
        this.loading = false
      }
    },
    async onConfirmDeleteUser() {
      this.loading = true;

      const [, error] = this.userDeleteModal.userId
        ? await this.request(`/api/v1/users/${this.userDeleteModal.userId}`, {
          method: 'patch',
          data: {
            data: {
              type: 'users',
              id: this.userDeleteModal.userId,
              relationships: {
                roles: {
                  data: []
                }
              }
            }
          } 
        })
        : await this.request(`/api/v1/roles/${this.roleUserModal.roleId}/-actions/remove-users`, {
          method: 'patch'
        });

      if (!error) {
        this.roleUserModal.reload = true
        this.roleUserModal.visible = true
        this.userDeleteModal.visible = false

        this.loadRoles({
          'page[number]': this.roles.meta.page.currentPage
        })
      }

      this.loading = false
    },
    async onExport() {
      this.startLoadingExport('Mengirim ke server');

      const [, err] = await this.request(
        `/api/v1/roles/-actions/export`,
        {
          method: 'post'
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });

        this.stopLoadingExport();
      } else {
        this.startLoadingExport('Sedang diproses');
      }
    },
    onExportProgress(e) {
      if (e.type === 'App\\Notifications\\RoleExported') {
        if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal mengexport hak akses',
            status: 'error',
          });
        } else if (e.status === 'success') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'hak_akses.csv', { blank: true });
        }
      }
    },
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
    async loadRoles(params) {
      this.loading = true;

      const [res, error] = await this.request('/api/v1/roles', {
        params: {
          'page[size]': 5,
          sort: 'name',
          'filter[search]': this.filter.search,
          'filter[office_type]': this.filter.office_type,
          'fields[roles]': 'name,office_type,description,users_count',
          ...params,
        },
      });

      if (!error) {
        this.roles = res;
      }

      this.loading = false;
    },
    async loadJobStatus() {
      this.loadingJobStatus = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'export_roles',
          },
        }
      );

      if (!err) {
        const { running } = res;

        if (running) {
          this.startLoadingExport('Sedang Diproses');
        }
      }

      this.loadingJobStatus = false;
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.visible = false;
    },
    removeExportListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onExportProgress
        );
      }
    },
  },
  created() {
    this.loadRoles();
    this.loadJobStatus();
    this.addListener()
  },
  beforeRouteLeave(to, from, next) {
    this.removeExportListener();

    next();
  },
};
</script>
